import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AlertTriangle, Check, Clock, Repeat } from 'lucide-react';
import {
  API, WORK_HOURS, formatTravelTime, timeToMinutes, minutesToTime,
  calculateLunchBuffer, isInLunchBreak, crossesLunchBreak, isOutsideWorkHours, getEstimatedDuration
} from '../utils';
import RouteMapPreview from './RouteMapPreview';

const ScheduleSection = ({ formData, setFormData, appointment, calculateTotalDuration }) => {
  const { t } = useTranslation();
  const [suggestedTime, setSuggestedTime] = useState(null);
  const [calculatingTime, setCalculatingTime] = useState(false);
  const [lastAppointmentInfo, setLastAppointmentInfo] = useState(null);
  const [eveningConstraint, setEveningConstraint] = useState(null);
  const [installerHomeAddress, setInstallerHomeAddress] = useState(null);

  const calculateTravelTime = async (fromAddress, toAddress) => {
    try {
      const tryGeocode = async (q) => {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
        const data = await res.json();
        if (data && data.length > 0) return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
        return null;
      };
      const geocodeAddress = async (address) => {
        let r = await tryGeocode(address);
        if (!r) {
          // Retry senza dettagli (es. "SNC", civici strani): usa solo citta' + provincia
          const parts = address.split(',').map(s => s.trim()).filter(Boolean);
          if (parts.length >= 2) r = await tryGeocode(`${parts.slice(-2).join(', ')}, Italia`);
        }
        return r;
      };
      const from = await geocodeAddress(fromAddress);
      const to = await geocodeAddress(toAddress);
      if (!from || !to) return 30;
      const routeRes = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${from.lon},${from.lat};${to.lon},${to.lat}?overview=false`
      );
      const routeData = await routeRes.json();
      if (routeData.routes && routeData.routes.length > 0) return Math.ceil(routeData.routes[0].duration / 60);
      // OSRM fallito ma coordinate note: stima da distanza in linea d'aria
      // (media 65 km/h + 25% strade reali) - molto meglio del vecchio flat 30 min
      const toRad = (deg) => (deg * Math.PI) / 180;
      const R = 6371;
      const dLat = toRad(to.lat - from.lat);
      const dLon = toRad(to.lon - from.lon);
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
      const distKm = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 1.25;
      return Math.max(10, Math.ceil((distKm / 65) * 60));
    } catch (e) { return 30; }
  };

  const calculateSuggestedTime = async () => {
    if (!formData.assigned_installer_id || !formData.scheduled_date || !formData.address || !formData.city) {
      setSuggestedTime(null);
      setLastAppointmentInfo(null);
      setEveningConstraint(null);
      setInstallerHomeAddress(null);
      return;
    }
    if (appointment) return;

    setCalculatingTime(true);
    try {
      const [installerRes, aptRes] = await Promise.all([
        axios.get(`${API}/users/${formData.assigned_installer_id}`),
        axios.get(`${API}/appointments?installer_id=${formData.assigned_installer_id}`)
      ]);
      const installer = installerRes.data;
      setInstallerHomeAddress(installer.starting_address || null);
      const allAppointments = aptRes.data;
      const selectedDate = formData.scheduled_date;
      let dayAppointments = allAppointments
        .filter(apt => apt.scheduled_date.split('T')[0] === selectedDate)
        // Exclude absences (Ferie/Malattia/Festivita/Altro) and cancelled/not_completed:
        // they should NOT push the suggested time of a real work appointment.
        .filter(apt => apt.intervention_type !== 'absence')
        .filter(apt => apt.status !== 'not_completed' && apt.status !== 'to_reschedule')
        .sort((a, b) => (a.scheduled_time || '00:00').localeCompare(b.scheduled_time || '00:00'));

      // Bug fix Veronica 2026-07-03 (issue #4):
      // Se nella giornata coesistono confermati E provvisori, i CONFERMATI hanno la
      // precedenza per il calcolo dell'orario suggerito. Un provvisorio "blocca giornata"
      // (es. 9-18) non deve piu' spingere il suggerimento alle 18 se ci sono confermati
      // piu' corti che coprono solo parte della fascia. Se ci sono SOLO provvisori,
      // il comportamento resta invariato (usa il provvisorio come baseline).
      const isConfirmedApt = (a) => {
        const cs = a.confirmation_status;
        // Un apt e' "confermato" se confirmation_status non e' provisional (default: confirmed)
        return cs !== 'provisional';
      };
      const confirmedOnly = dayAppointments.filter(isConfirmedApt);
      if (confirmedOnly.length > 0) {
        dayAppointments = confirmedOnly;
      }

      const newAddress = `${formData.address}, ${formData.city}, ${formData.province}`;
      const COMMUTE_BUFFER = WORK_HOURS.commuteAllowance;
      const WORK_START = timeToMinutes(WORK_HOURS.morning.start);
      const WORK_END = timeToMinutes(WORK_HOURS.afternoon.end);

      let suggestedMinutes;

      if (dayAppointments.length === 0) {
        // === PRIMO APPUNTAMENTO - REGOLA MATTINA ===
        if (installer.starting_address) {
          const travelTime = await calculateTravelTime(installer.starting_address, newAddress);

          // Solo il viaggio che ECCEDE il buffer pendolarismo sposta l'orario
          if (travelTime > COMMUTE_BUFFER) {
            suggestedMinutes = WORK_START + (travelTime - COMMUTE_BUFFER);
          } else {
            suggestedMinutes = WORK_START;
          }

          const lunchBuffer = calculateLunchBuffer(minutesToTime(WORK_START), travelTime);
          suggestedMinutes += lunchBuffer;
          if (suggestedMinutes >= timeToMinutes("12:30") && suggestedMinutes < timeToMinutes("13:30")) {
            suggestedMinutes = timeToMinutes("13:30");
          }

          const isToday = selectedDate === new Date().toISOString().split('T')[0];
          if (isToday) {
            const now = new Date();
            const nowMinutes = now.getHours() * 60 + now.getMinutes() + 15;
            if (suggestedMinutes < nowMinutes) {
              suggestedMinutes = Math.ceil(nowMinutes / 30) * 30;
              if (suggestedMinutes >= timeToMinutes("12:30") && suggestedMinutes < timeToMinutes("13:30")) {
                suggestedMinutes = timeToMinutes("13:30");
              }
            }
          }

          setSuggestedTime(minutesToTime(suggestedMinutes));
          setLastAppointmentInfo({
            type: 'first', fromAddress: installer.starting_address,
            travelTime, commuteBuffer: COMMUTE_BUFFER,
            effectiveTravel: Math.max(0, travelTime - COMMUTE_BUFFER),
            lunchBuffer
          });
        } else {
          suggestedMinutes = WORK_START;
          const isToday = selectedDate === new Date().toISOString().split('T')[0];
          if (isToday) {
            const now = new Date();
            const nowMinutes = now.getHours() * 60 + now.getMinutes() + 15;
            if (suggestedMinutes < nowMinutes) {
              suggestedMinutes = Math.ceil(nowMinutes / 30) * 30;
              if (suggestedMinutes >= timeToMinutes("12:30") && suggestedMinutes < timeToMinutes("13:30")) {
                suggestedMinutes = timeToMinutes("13:30");
              }
            }
          }
          setSuggestedTime(minutesToTime(suggestedMinutes));
          setLastAppointmentInfo({ type: 'first', travelTime: 0, commuteBuffer: COMMUTE_BUFFER, effectiveTravel: 0 });
        }
      } else {
        // === APPUNTAMENTO SUCCESSIVO: cerca il PRIMO SLOT LIBERO della giornata ===
        // Bug Marisa 2026-07-07: prima considerava sempre e solo l'ultimo appuntamento
        // della giornata, ignorando i buchi liberi (es. mattina libera tra le 9 e le 15).
        // Ora scorre i "buchi" in ordine cronologico: per ciascuno calcola il viaggio
        // dall'appuntamento PRECEDENTE e verifica che il nuovo (durata + viaggio verso
        // il successivo) ci stia prima dell'inizio del prossimo appuntamento.
        const getAptEndMinutes = (apt) => {
          if (apt.scheduled_end_time) {
            const [eh, em] = apt.scheduled_end_time.split(':').map(Number);
            return eh * 60 + em;
          }
          const [sh, sm] = (apt.scheduled_time || '00:00').split(':').map(Number);
          const dur = apt.total_duration || apt.estimated_duration || getEstimatedDuration(apt.device_type) || 60;
          return sh * 60 + sm + dur;
        };
        const getAptStartMinutes = (apt) => timeToMinutes(apt.scheduled_time || '00:00');
        const durationForFit = (calculateTotalDuration && calculateTotalDuration()) || 60;
        const isToday = selectedDate === new Date().toISOString().split('T')[0];
        const now = new Date();
        const nowLimit = now.getHours() * 60 + now.getMinutes() + 15;
        // Se lo slot attraversa la pausa pranzo, il tempo occupato include +60 min
        // (coerente con la fine prevista che include la pausa)
        const lunchExtra = (startMin) =>
          (startMin < timeToMinutes("12:30") && (startMin + durationForFit) > timeToMinutes("12:30")) ? 60 : 0;

        let chosen = null;

        // === GAP 0: lo spazio VUOTO PRIMA del primo appuntamento della giornata ===
        // Caso Veronica/Valentina 2026-07-09 (Star Emilia Ferrara): mattina vuota e unico
        // appuntamento al pomeriggio -> il sistema proponeva dopo il pomeriggio invece
        // della mattina libera. Qui applichiamo la "regola mattina" (viaggio da casa
        // tecnico) e verifichiamo che il nuovo ci stia PRIMA del primo appuntamento.
        {
          const firstApt = dayAppointments[0];
          const firstStart = getAptStartMinutes(firstApt);
          let travelFromHome = 0;
          let candidate0 = WORK_START;
          let lunchBuffer0 = 0;
          if (installer.starting_address) {
            travelFromHome = await calculateTravelTime(installer.starting_address, newAddress);
            candidate0 = travelFromHome > COMMUTE_BUFFER
              ? WORK_START + (travelFromHome - COMMUTE_BUFFER)
              : WORK_START;
            lunchBuffer0 = calculateLunchBuffer(minutesToTime(WORK_START), travelFromHome);
            candidate0 += lunchBuffer0;
          }
          if (candidate0 >= timeToMinutes("12:30") && candidate0 < timeToMinutes("13:30")) {
            candidate0 = timeToMinutes("13:30");
          }
          if (isToday && candidate0 < nowLimit) {
            candidate0 = Math.ceil(nowLimit / 30) * 30;
            if (candidate0 >= timeToMinutes("12:30") && candidate0 < timeToMinutes("13:30")) {
              candidate0 = timeToMinutes("13:30");
            }
          }
          const firstAddress = `${firstApt.address}, ${firstApt.city}, ${firstApt.province}`;
          const travelToFirst = await calculateTravelTime(newAddress, firstAddress);
          if (candidate0 + durationForFit + lunchExtra(candidate0) + travelToFirst <= firstStart) {
            chosen = {
              kind: 'gap0', candidate: candidate0,
              travelTime: travelFromHome, lunchBuffer: lunchBuffer0,
              beforeApt: firstApt
            };
          }
        }

        for (let i = 0; i < dayAppointments.length && !chosen; i++) {
          const prevApt = dayAppointments[i];
          const nextApt = dayAppointments[i + 1] || null;
          const prevEnd = getAptEndMinutes(prevApt);
          // Nessun buco tra questo e il successivo
          if (nextApt && getAptStartMinutes(nextApt) <= prevEnd) continue;

          const fromAddress = `${prevApt.address}, ${prevApt.city}, ${prevApt.province}`;
          const travelTime = await calculateTravelTime(fromAddress, newAddress);
          const lunchBuffer = calculateLunchBuffer(minutesToTime(prevEnd), travelTime);
          let candidate = prevEnd + travelTime + lunchBuffer;
          if (candidate >= timeToMinutes("12:30") && candidate < timeToMinutes("13:30")) {
            candidate = timeToMinutes("13:30");
          }
          if (isToday && candidate < nowLimit) {
            candidate = Math.ceil(nowLimit / 30) * 30;
            if (candidate >= timeToMinutes("12:30") && candidate < timeToMinutes("13:30")) {
              candidate = timeToMinutes("13:30");
            }
          }

          if (nextApt) {
            // Deve finire (durata + eventuale pausa pranzo + viaggio verso il prossimo)
            // prima dell'inizio del prossimo appuntamento
            const nextAddress = `${nextApt.address}, ${nextApt.city}, ${nextApt.province}`;
            const travelToNext = await calculateTravelTime(newAddress, nextAddress);
            if (candidate + durationForFit + lunchExtra(candidate) + travelToNext > getAptStartMinutes(nextApt)) continue;
          }
          chosen = { kind: 'gap', candidate, prevApt, prevEnd, travelTime, lunchBuffer, fromAddress };
        }

        // L'ultimo "buco" (dopo l'ultimo appuntamento) viene sempre scelto se i precedenti non bastano
        suggestedMinutes = chosen.candidate;
        setSuggestedTime(minutesToTime(suggestedMinutes));
        if (chosen.kind === 'gap0') {
          setLastAppointmentInfo({
            type: 'first',
            fromAddress: installer.starting_address || null,
            travelTime: chosen.travelTime,
            commuteBuffer: COMMUTE_BUFFER,
            effectiveTravel: Math.max(0, chosen.travelTime - COMMUTE_BUFFER),
            lunchBuffer: chosen.lunchBuffer,
            beforeAppointment: chosen.beforeApt
          });
        } else {
          setLastAppointmentInfo({
            type: 'continuation', lastAppointment: chosen.prevApt,
            lastEndTime: minutesToTime(chosen.prevEnd), travelTime: chosen.travelTime,
            fromAddress: chosen.fromAddress, lunchBuffer: chosen.lunchBuffer
          });
        }
      }

      // === REGOLA SERA: Vincolo viaggio di ritorno verso casa tecnico ===
      // Compute evening constraint FIRST so we can also use it to clamp the suggestion.
      let latestEndMinutesForClamp = WORK_END;
      if (installer.starting_address) {
        const returnTravelTime = await calculateTravelTime(newAddress, installer.starting_address);
        let latestEndMinutes;
        if (returnTravelTime > COMMUTE_BUFFER) {
          latestEndMinutes = WORK_END - (returnTravelTime - COMMUTE_BUFFER);
        } else {
          latestEndMinutes = WORK_END;
        }
        latestEndMinutesForClamp = latestEndMinutes;
        setEveningConstraint({
          returnTravelTime,
          latestEndMinutes,
          latestEndTime: minutesToTime(latestEndMinutes),
          commuteBuffer: COMMUTE_BUFFER,
          excessTravel: Math.max(0, returnTravelTime - COMMUTE_BUFFER)
        });
      } else {
        setEveningConstraint(null);
      }

      // === CLAMP FINALE: se il suggerimento va oltre l'orario di fine giornata, non proporlo ===
      // Nota: un appuntamento PUO' attraversare la pausa pranzo (la fine include +60 min),
      // quindi il clamp considera lo span effettivo invece di shiftare a dopo pranzo.
      const estimatedDurationForClamp = (calculateTotalDuration && calculateTotalDuration()) || 60;
      const crossesLunchForClamp = suggestedMinutes < timeToMinutes("12:30") &&
        (suggestedMinutes + estimatedDurationForClamp) > timeToMinutes("12:30");
      const spanForClamp = estimatedDurationForClamp + (crossesLunchForClamp ? 60 : 0);
      if (suggestedMinutes + spanForClamp > latestEndMinutesForClamp) {
        // Non c'è più spazio nella giornata: mostra info ma non forzare un orario assurdo.
        setSuggestedTime(null);
      } else {
        setSuggestedTime(minutesToTime(suggestedMinutes));
      }
    } catch (e) {
      console.error("Error calculating suggested time:", e);
      setSuggestedTime(null);
    } finally {
      setCalculatingTime(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => calculateSuggestedTime(), 500);
    return () => clearTimeout(timer);
  }, [formData.assigned_installer_id, formData.scheduled_date, formData.address, formData.city, formData.province]);

  // Auto-calculate end time from start time + total duration.
  // Se l'appuntamento attraversa la pausa pranzo (12:30-13:30), la fine prevista
  // include 1h di pausa. SALTA il ricalcolo se l'operatore ha modificato a mano
  // l'Ora Fine (bug Veronica 2026-07-09: formazione piu' lunga della durata dispositivi).
  useEffect(() => {
    if (formData.end_time_manual) return;
    if (formData.scheduled_time && calculateTotalDuration) {
      const totalMin = calculateTotalDuration();
      if (totalMin > 0) {
        const [h, m] = formData.scheduled_time.split(':').map(Number);
        const startMin = h * 60 + m;
        let endMin = startMin + totalMin;
        const lunchStart = timeToMinutes("12:30");
        if (startMin < lunchStart && endMin > lunchStart) {
          endMin += 60; // il tecnico si ferma 12:30-13:30
        }
        const endH = Math.floor(endMin / 60) % 24;
        const endM = endMin % 60;
        const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
        setFormData(prev => {
          if (prev.scheduled_end_time !== endTime) return { ...prev, scheduled_end_time: endTime };
          return prev;
        });
      }
    }
  });

  const applySuggestedTime = () => {
    if (suggestedTime) setFormData(prev => ({ ...prev, scheduled_time: suggestedTime }));
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Data *</label>
        <input
          type="date"
          value={formData.scheduled_date}
          onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
          required
          data-testid="appointment-date"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Ora Inizio *</label>
        <div className="flex gap-2">
          <input
            type="time"
            value={formData.scheduled_time}
            onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
            required
            data-testid="appointment-time"
          />
          {suggestedTime && !appointment && (
            <button type="button" onClick={applySuggestedTime}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium whitespace-nowrap">
              {suggestedTime}
            </button>
          )}
          {!appointment && formData.assigned_installer_id && formData.scheduled_date && formData.address && (
            <button
              type="button"
              onClick={() => calculateSuggestedTime()}
              disabled={calculatingTime}
              className="px-2 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm border border-gray-300 disabled:opacity-50"
              title="Ricalcola l'orario suggerito (utile dopo aver modificato/cancellato altri appuntamenti nella stessa giornata)"
              data-testid="recalc-suggested-time-btn"
            >
              🔄
            </button>
          )}
          {calculatingTime && (
            <span className="px-3 py-2 text-gray-500 text-sm flex items-center">Calcolo...</span>
          )}
        </div>

        {/* Suggested time info */}
        {lastAppointmentInfo && !appointment && (
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            {lastAppointmentInfo.type === 'first' ? (
              <div className="text-blue-800">
                <p className="font-medium">Primo appuntamento del giorno</p>
                {lastAppointmentInfo.beforeAppointment && (
                  <p className="text-blue-600 mt-1" data-testid="suggested-before-apt">
                    Proposto nello slot libero PRIMA di {lastAppointmentInfo.beforeAppointment.client_name} delle {lastAppointmentInfo.beforeAppointment.scheduled_time}
                  </p>
                )}
                {lastAppointmentInfo.fromAddress && (
                  <p className="text-blue-600 mt-1">Partenza da: {lastAppointmentInfo.fromAddress.substring(0, 40)}...</p>
                )}
                <p className="text-blue-600">Tempo viaggio: ~{formatTravelTime(lastAppointmentInfo.travelTime)}</p>
                {lastAppointmentInfo.travelTime > 0 && (
                  <p className="text-blue-600">
                    Buffer pendolarismo: {formatTravelTime(lastAppointmentInfo.commuteBuffer)} incluso
                    {lastAppointmentInfo.effectiveTravel > 0
                      ? ` (eccedenza: +${formatTravelTime(lastAppointmentInfo.effectiveTravel)})`
                      : ' (viaggio coperto dal buffer)'}
                  </p>
                )}
                {lastAppointmentInfo.lunchBuffer > 0 && (
                  <p className="text-orange-600 font-medium mt-1">
                    + {formatTravelTime(lastAppointmentInfo.lunchBuffer)} pausa pranzo
                  </p>
                )}
                {suggestedTime ? (
                  <p className="font-semibold text-green-700 mt-1">Orario suggerito: {suggestedTime}</p>
                ) : (
                  <p className="font-semibold text-red-700 mt-1">Nessuno slot disponibile in giornata per la durata prevista</p>
                )}
              </div>
            ) : (
              <div className="text-blue-800">
                <p className="font-medium">Appuntamento precedente: {lastAppointmentInfo.lastAppointment?.client_name}</p>
                <p className="text-blue-600 mt-1">Fine prevista: {lastAppointmentInfo.lastEndTime}</p>
                <p className="text-blue-600">Viaggio da: {lastAppointmentInfo.fromAddress?.substring(0, 30)}...</p>
                <p className="text-blue-600">Tempo viaggio stimato: ~{formatTravelTime(lastAppointmentInfo.travelTime)}</p>
                {lastAppointmentInfo.lunchBuffer > 0 && (
                  <p className="text-orange-600 font-medium mt-1">
                    + {formatTravelTime(lastAppointmentInfo.lunchBuffer)} pausa pranzo (viaggio attraversa 12:00-14:00)
                  </p>
                )}
                {suggestedTime ? (
                  <p className="font-semibold text-green-700 mt-1">Orario suggerito: {suggestedTime}</p>
                ) : (
                  <p className="font-semibold text-red-700 mt-1">Nessuno slot disponibile in giornata per la durata prevista</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Evening constraint info */}
        {eveningConstraint && !appointment && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="evening-constraint-info">
            <div className="text-amber-800">
              <p className="font-medium flex items-center gap-1">
                <Clock size={14} /> Vincolo Orario Serale
              </p>
              <p className="text-amber-600 mt-1">
                Viaggio di ritorno stimato: ~{formatTravelTime(eveningConstraint.returnTravelTime)}
              </p>
              {eveningConstraint.excessTravel > 0 ? (
                <p className="text-amber-600">
                  Eccedenza buffer: +{formatTravelTime(eveningConstraint.excessTravel)} &rarr; Fine lavoro anticipata
                </p>
              ) : (
                <p className="text-amber-600">
                  Viaggio coperto dal buffer pendolarismo ({formatTravelTime(eveningConstraint.commuteBuffer)})
                </p>
              )}
              <p className="font-semibold text-amber-900 mt-1">
                Ora fine massima appuntamento: {eveningConstraint.latestEndTime}
              </p>
            </div>
          </div>
        )}

        {/* Evening limit exceeded warning */}
        {eveningConstraint && formData.scheduled_end_time && !appointment &&
          timeToMinutes(formData.scheduled_end_time) > eveningConstraint.latestEndMinutes && (
          <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2" data-testid="evening-limit-warning">
            <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <p className="font-semibold text-red-800">Appuntamento oltre il limite serale</p>
              <p className="text-red-600">
                Fine prevista: {formData.scheduled_end_time} — Limite: {eveningConstraint.latestEndTime}
              </p>
              <p className="text-red-600">
                Il tecnico non riuscirebbe a rientrare entro le 17:30
              </p>
            </div>
          </div>
        )}

        {/* Work hours warnings */}
        {formData.scheduled_time && (
          <>
            {(() => {
              const totalMin = calculateTotalDuration ? calculateTotalDuration() : 0;
              const startsInLunch = isInLunchBreak(formData.scheduled_time);
              const crossesLunch = crossesLunchBreak(formData.scheduled_time, totalMin);
              if (startsInLunch || crossesLunch) {
                return (
                  <div className="mt-2 p-3 bg-orange-50 border border-orange-300 rounded-lg flex items-start gap-2" data-testid="lunch-break-warning">
                    <AlertTriangle className="text-orange-500 flex-shrink-0 mt-0.5" size={18} />
                    <div className="text-sm">
                      <p className="font-semibold text-orange-800">
                        {startsInLunch ? 'Orario in pausa pranzo (12:30-13:30)' : 'Appuntamento attraversa la pausa pranzo'}
                      </p>
                      <p className="text-orange-600">
                        {startsInLunch
                          ? "L'appuntamento è programmato durante la pausa pranzo standard."
                          : `Inizio ${formData.scheduled_time} + durata ${Math.floor(totalMin/60)}h${totalMin%60 ? ' ' + (totalMin%60) + 'min' : ''} sovrappone la fascia 12:30-13:30.`}
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            {isOutsideWorkHours(formData.scheduled_time) && !isInLunchBreak(formData.scheduled_time) && (
              <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2">
                <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm">
                  <p className="font-semibold text-red-800">Orario fuori orario lavorativo</p>
                  <p className="text-red-600">Orario di lavoro: 08:30-12:30 e 13:30-17:30</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Route Map Preview */}
      {!appointment && installerHomeAddress && formData.address && formData.city && (
        <div className="col-span-2">
          <RouteMapPreview
            installerAddress={installerHomeAddress}
            appointmentAddress={`${formData.address}, ${formData.city}${formData.province ? ', ' + formData.province : ''}`}
            forwardTravelTime={lastAppointmentInfo?.travelTime}
            returnTravelTime={eveningConstraint?.returnTravelTime}
          />
        </div>
      )}

      {/* Ora Fine */}
      <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Ora Fine</label>
        <div className="flex items-center gap-3">
          <input
            type="time"
            value={formData.scheduled_end_time}
            onChange={(e) => setFormData(prev => ({ ...prev, scheduled_end_time: e.target.value, end_time_manual: true }))}
            className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
            data-testid="appointment-end-time"
          />
          {formData.end_time_manual && (
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, end_time_manual: false }))}
              className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
              data-testid="end-time-recalc-btn"
            >
              ↻ Ricalcola automatico
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-1">
          {formData.end_time_manual
            ? 'Orario impostato manualmente: il sistema non lo ricalcolerà'
            : 'Calcolato in automatico dalla durata dispositivo, modificabile manualmente'}
        </p>
      </div>

      {/* Stato Conferma */}
      <div className="col-span-2 mt-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Stato Conferma</label>
        <div className="flex gap-3">
          <button type="button"
            onClick={() => setFormData(prev => ({ ...prev, confirmation_status: 'confirmed' }))}
            className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition ${
              formData.confirmation_status === 'confirmed'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:border-blue-300'
            }`}
            data-testid="confirmation-confirmed">
            <Check size={16} className="inline mr-1" /> Confermato
          </button>
          <button type="button"
            onClick={() => setFormData(prev => ({ ...prev, confirmation_status: 'provisional' }))}
            className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition ${
              formData.confirmation_status === 'provisional'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 text-gray-600 hover:border-purple-300'
            }`}
            data-testid="confirmation-provisional">
            <Clock size={16} className="inline mr-1" /> Provvisorio
          </button>
        </div>
      </div>

      {/* Ricorrenza - solo in creazione */}
      {!appointment && (
        <div className="col-span-2 mt-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Ricorrenza</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {[
              { key: 'none', label: 'Nessuna' },
              { key: 'weekly', label: 'Settimanale' },
              { key: 'biweekly', label: 'Quindicinale' },
              { key: 'monthly', label: 'Mensile' },
            ].map(opt => (
              <button key={opt.key} type="button"
                onClick={() => setFormData(prev => ({ ...prev, recurrence_type: opt.key }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition border ${
                  formData.recurrence_type === opt.key
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600 hover:border-green-300'
                }`}
                data-testid={`recurrence-${opt.key}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {formData.recurrence_type !== 'none' && (
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Repeat size={16} className="text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <label className="block text-xs font-medium text-green-700 mb-1">Ripeti fino a</label>
                <input
                  type="date"
                  value={formData.recurrence_end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, recurrence_end_date: e.target.value }))}
                  min={formData.scheduled_date}
                  className="w-full px-3 py-1.5 border border-green-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                  required
                  data-testid="recurrence-end-date"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleSection;
